import { Button, Flex } from "@mantine/core";
import type { JSX } from "react";
import { useState } from "react";
import { useAnalytics, withPolymorphicClickAnalytics } from "@/analytics";
import { usePartner, useSessionId } from "@/sdk";
import { postDeclination } from "../../api";
import type { EligibleProduct } from "../../stores";
import { useProductOfferStore } from "../../stores";
import { useIntegratedFlowStore } from "../../stores/useIntegratedFlowStore";
import { SaleProgress } from "../../types";
import { RightArrowIcon } from "../Icons";
import type { IntegratedSalesSteps } from "../types";
import classes from "./CollectCustomerInfo.module.css";

interface CollectCustomerInfoFooterProps {
    product: EligibleProduct | undefined;
    isCustomerInfoCollected: boolean;
    isLoadingEligibility: boolean;
    integratedStep: IntegratedSalesSteps;
    handleContinueToPayment: () => Promise<void>;
}

export const CollectCustomerInfoFooter = ({
    product,
    isCustomerInfoCollected,
    isLoadingEligibility,
    integratedStep,
    handleContinueToPayment,
}: CollectCustomerInfoFooterProps): JSX.Element => {
    const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "ButtonClick");
    const [declining, setDeclining] = useState(false);
    const updateProduct = useProductOfferStore((state) => state.updateProduct);
    const { dispatcher } = useAnalytics();
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const partner = usePartner();
    const sessionId = useSessionId();
    const decline = async () => {
        try {
            if (product) {
                await postDeclination({
                    sessionId,
                    partner,
                    productOfferId: product.id,
                    productSku: product.productSku,
                });
                updateProduct(sessionId, product.id, { saleProgress: SaleProgress.Declined });
            }
            setDeclining(false);
        } catch {
            setDeclining(false);
        }
    };
    const handleBackClick = () => {
        void dispatcher
            .withExtra({
                nextStep: integratedStep,
            })
            .dispatchBusinessEvent("ButtonClicked_Back");
        updateIntegratedStep(sessionId, "SuggestedOfferStep");
    };
    return (
        <Flex className={classes.footerBox}>
            <Flex justify={"left"} align={"center"}>
                <Button
                    size="xs"
                    fz="xs"
                    color="dark.1"
                    leftSection={<RightArrowIcon height="12" width="12" x="-1" />}
                    variant="transparent"
                    onClick={handleBackClick}
                >
                    Back
                </Button>
            </Flex>
            <Flex align="center" justify={"right"} gap="xs">
                <AnalyticsButton
                    analytics={{
                        actionId: "CollectInfoBackButton",
                    }}
                    color="dark.1"
                    bd="dark.1"
                    fz="xs"
                    loading={declining}
                    variant="transparent"
                    data-testid="collect-info-back-button"
                    size="xs"
                    onClick={() => {
                        setDeclining(true);
                        void decline();
                    }}
                >
                    <u>Decline</u>
                </AnalyticsButton>
                <Button
                    size="xs"
                    disabled={!isCustomerInfoCollected}
                    className={isCustomerInfoCollected ? classes.enabledButton : classes.disabledButton}
                    variant="outline"
                    fz="xs"
                    data-testid="continue-to-payment"
                    loading={isLoadingEligibility}
                    onClick={() => {
                        void handleContinueToPayment();
                    }}
                >
                    Continue
                </Button>
            </Flex>
        </Flex>
    );
};
